import React, { Component } from "react";
import { Upload, Table, Tag, Alert, Typography, message, Select, Modal, Progress, Descriptions } from "antd";
import { UploadOutlined, FileExcelOutlined, DownloadOutlined, WarningOutlined } from "@ant-design/icons";
import axios from "axios";
import * as XLSX from "xlsx";
import moment from "moment";
import { ModalItem, ButtonItem } from "../Items";
import {
    descargarFormatoRequerimiento,
    leerFormatoRequerimiento,
    normalizarIsbn,
    normalizarTexto,
} from "./formatoRequerimientoExcel";

const { Text } = Typography;
const { Option } = Select;

const TAMANO_LOTE_ISBN = 500;
const TAMANO_LOTE_CLAVES = 200;

const fechaVisible = (texto) => (texto ? moment(texto, "YYYY-MM-DD").format("DD/MM/YYYY") : "—");

// Sube el Formato Unico de Requerimiento que lleno el cliente (complemento
// formatoRequerimientoCotizaciones). Resuelve cada ISBN en dos pasos:
// 1) cotizaciones/resolverIsbnsFormatoRequerimiento: ISBN -> insumos candidatos
//    (codigo de barras y, si no, clave);
// 2) insumos/resolverClavesInsumosVentaCompletoMasivo con la clave de esos candidatos,
//    para que el insumo llegue con la forma completa que espera la pantalla.
// Se cruza por insumoId, no por clave: dos insumos pueden compartir la clave.
class ModalFormatoRequerimiento extends Component {
    state = {
        cargando: false,
        progreso: 0,
        archivoNombre: null,
        lectura: null, // { encabezado, destinos, avisos }
        filas: [],
        catalogo: {}, // insumoId -> insumo completo
    };

    componentDidUpdate(prevProps) {
        if (this.props.visible && !prevProps.visible) {
            this.setState({
                cargando: false,
                progreso: 0,
                archivoNombre: null,
                lectura: null,
                filas: [],
                catalogo: {},
            });
        }
    }

    // ---------------------------------------------------------------- lectura

    onSubirArchivo = (info) => {
        const file = info.file;
        if (!/\.xlsx?$/i.test(file.name || "")) {
            message.error("El archivo debe ser un Excel (.xlsx/.xls)");
            return;
        }
        this.setState({ cargando: true, progreso: 0, archivoNombre: file.name, lectura: null, filas: [], catalogo: {} });

        const reader = new FileReader();
        reader.onloadend = (e) => {
            let lectura;
            try {
                const libro = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
                const hoja = libro.Sheets[libro.SheetNames[0]];
                lectura = leerFormatoRequerimiento(
                    XLSX.utils.sheet_to_json(hoja, { header: 1, defval: "", raw: true })
                );
            } catch (err) {
                console.error(err);
                message.error("No se pudo leer el archivo. Verifica que sea un Excel válido.");
                this.setState({ cargando: false });
                return;
            }
            if (!lectura.esFormato) {
                message.error('No parece el Formato Único de Requerimiento: no se encontró la columna "ISBN".');
                this.setState({ cargando: false });
                return;
            }
            if (lectura.productos.length === 0) {
                message.warning("El formato no trae productos.");
            }
            this.resolverProductos(lectura);
        };
        reader.readAsArrayBuffer(file.originFileObj || file);
    };

    resolverProductos = async (lectura) => {
        const { empresaId, headersToken, clienteId, precioId } = this.props;
        const { encabezado, destinos, avisos, productos } = lectura;

        const isbns = [...new Set(productos.filter((p) => !p.error).map((p) => normalizarIsbn(p.isbn)))].filter(Boolean);
        const candidatosPorIsbn = new Map();
        const catalogo = {};

        try {
            // 1) ISBN -> candidatos
            for (let inicio = 0; inicio < isbns.length; inicio += TAMANO_LOTE_ISBN) {
                const lote = isbns.slice(inicio, inicio + TAMANO_LOTE_ISBN);
                const res = await axios.post(
                    "cotizaciones/resolverIsbnsFormatoRequerimiento",
                    { empresaId, isbns: lote },
                    { headers: headersToken }
                );
                if (res.data?.success !== 2) {
                    message.error(res.data?.message || "No se pudieron buscar los ISBN del formato");
                    this.setState({ cargando: false });
                    return;
                }
                (res.data.resultados || []).forEach((resultado, i) => {
                    candidatosPorIsbn.set(lote[i], resultado.candidatos || []);
                });
                this.setState({ progreso: Math.round(((inicio + lote.length) / Math.max(isbns.length, 1)) * 40) });
            }

            // 2) clave de los candidatos -> insumo completo. Sin descripcion a proposito:
            // si la clave se repite, el resolver devuelve TODOS los candidatos (AMBIGUO)
            // y aqui se elige el que encontro el ISBN, en vez de que el resolver
            // desempate por su cuenta y deje fuera al correcto.
            const claves = [
                ...new Set(
                    [...candidatosPorIsbn.values()].flat().map((c) => c.clave).filter((c) => c && c.trim())
                ),
            ];
            for (let inicio = 0; inicio < claves.length; inicio += TAMANO_LOTE_CLAVES) {
                const lote = claves.slice(inicio, inicio + TAMANO_LOTE_CLAVES);
                const res = await axios.post(
                    "insumos/resolverClavesInsumosVentaCompletoMasivo",
                    {
                        empresaId,
                        clienteId: clienteId || null,
                        precioId: precioId || null,
                        filas: lote.map((clave) => ({ clave, descripcion: "" })),
                    },
                    { headers: headersToken }
                );
                if (res.data?.success !== 2) {
                    message.error(res.data?.message || "No se pudieron resolver los insumos del formato");
                    this.setState({ cargando: false });
                    return;
                }
                (res.data.insumos || []).forEach((insumo) => {
                    catalogo[String(insumo._id)] = insumo;
                });
                this.setState({
                    progreso: 40 + Math.round(((inicio + lote.length) / claves.length) * 60),
                });
            }
        } catch (err) {
            console.error(err);
            message.error("Error de conexión al resolver los ISBN del formato");
            this.setState({ cargando: false });
            return;
        }

        const filas = productos.map((producto) => {
            if (producto.error) {
                return { ...producto, estado: "error", motivo: producto.error, insumo: null, candidatos: [] };
            }
            const candidatos = candidatosPorIsbn.get(normalizarIsbn(producto.isbn)) || [];
            if (candidatos.length === 0) {
                return {
                    ...producto,
                    estado: "no_encontrado",
                    motivo: "No hay un insumo de venta con ese ISBN en código de barras ni en clave",
                    insumo: null,
                    candidatos: [],
                };
            }
            const disponibles = candidatos.map((c) => catalogo[c.insumoId]).filter(Boolean);
            if (disponibles.length === 0) {
                const sinClave = candidatos.some((c) => !c.clave || !c.clave.trim());
                return {
                    ...producto,
                    estado: "no_encontrado",
                    motivo: sinClave
                        ? `"${candidatos[0].descripcion}" no tiene clave; captúrale una clave en el catálogo`
                        : `"${candidatos[0].descripcion}" no está disponible para este cliente`,
                    insumo: null,
                    candidatos: [],
                };
            }
            if (disponibles.length === 1) {
                return { ...producto, estado: "encontrado", insumo: disponibles[0], candidatos: [] };
            }
            // Varios insumos con el mismo ISBN: la descripcion del formato desempata.
            const descripcion = normalizarTexto(producto.descripcion);
            const porDescripcion = descripcion
                ? disponibles.filter((i) => normalizarTexto(i.descripcion) === descripcion)
                : [];
            if (porDescripcion.length === 1) {
                return { ...producto, estado: "encontrado", insumo: porDescripcion[0], candidatos: [] };
            }
            return { ...producto, estado: "ambiguo", insumo: null, candidatos: disponibles };
        });

        this.setState({
            lectura: { encabezado, destinos, avisos },
            filas,
            catalogo,
            cargando: false,
            progreso: 100,
        });
    };

    // Por la fila del Excel y no por el indice que da la tabla: con paginacion ese
    // indice es el de la pagina, no el del arreglo.
    elegirCandidato = (filaExcel, insumoId) => {
        this.setState((prev) => {
            const insumo = prev.catalogo[String(insumoId)];
            if (!insumo) return null;
            const filas = prev.filas.map((f) =>
                f.fila === filaExcel ? { ...f, estado: "encontrado", insumo, candidatos: [] } : f
            );
            return { filas };
        });
    };

    descargarNoCargados = () => {
        const noCargadas = this.state.filas.filter((f) => f.estado !== "encontrado");
        if (noCargadas.length === 0) return;
        const hoja = XLSX.utils.json_to_sheet(
            noCargadas.map((f) => ({
                Fila: f.fila,
                ISBN: f.isbn,
                Descripción: f.descripcion,
                Edición: f.edicion,
                Cantidad: f.cantidad,
                Motivo: f.estado === "ambiguo" ? "Varios insumos con ese ISBN, falta elegir uno" : f.motivo,
            }))
        );
        hoja["!cols"] = [{ wch: 6 }, { wch: 16 }, { wch: 50 }, { wch: 10 }, { wch: 10 }, { wch: 60 }];
        const libro = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(libro, hoja, "No cargados");
        XLSX.writeFile(libro, "Formato_Requerimiento_No_Cargados.xlsx");
    };

    // -------------------------------------------------------------- confirmar

    filasValidas = () => this.state.filas.filter((f) => f.estado === "encontrado" && f.insumo);

    confirmar = (modo) => {
        const validas = this.filasValidas();
        if (validas.length === 0) {
            message.error("No hay renglones válidos para cargar");
            return;
        }
        const { lectura } = this.state;
        const cargar = () =>
            this.props.onConfirmar({
                encabezado: lectura.encabezado,
                destinos: lectura.destinos,
                filas: validas.map((f) => ({
                    insumo: f.insumo,
                    cantidad: f.cantidad,
                    // null = sin precio en el formato: la pagina usa la lista de precios
                    precioExcel: null,
                    descuento: f.descuento || 0,
                    destino: f.destino,
                    edicion: f.edicion,
                })),
                modo,
            });

        if (modo === "reemplazar") {
            Modal.confirm({
                title: "Reemplazar los insumos capturados",
                icon: <WarningOutlined style={{ color: "#faad14" }} />,
                content: `Se van a borrar los ${this.props.cantidadInsumosCapturados || 0} renglón(es) capturados y quedarán sólo los ${validas.length} del formato.`,
                okText: "Reemplazar",
                cancelText: "Cancelar",
                onOk: cargar,
            });
            return;
        }
        cargar();
    };

    // ----------------------------------------------------------------- render

    columnasTabla = () => [
        { title: "Fila", dataIndex: "fila", width: 50 },
        { title: "ISBN", dataIndex: "isbn", width: 120 },
        {
            title: "Insumo",
            render: (_, fila) =>
                fila.insumo ? (
                    <span>{fila.insumo.descripcion}</span>
                ) : (
                    <Text type="secondary">{fila.descripcion || "—"}</Text>
                ),
        },
        { title: "Edición", dataIndex: "edicion", width: 70, render: (v) => v || "—" },
        { title: "Cant.", dataIndex: "cantidad", width: 60 },
        { title: "Destino", dataIndex: "destino", width: 70, render: (v) => (v === null ? "—" : v) },
        // El formato del cliente no trae descuento; solo el que regresa Biwo, si lo vuelven a subir.
        ...(this.state.filas.some((f) => f.descuento)
            ? [{ title: "Desc.", dataIndex: "descuento", width: 60, render: (v) => (v ? `${v}%` : "—") }]
            : []),
        {
            title: "Estado",
            width: 280,
            render: (_, fila) => {
                if (fila.estado === "error") return <Tag color="red">{fila.motivo}</Tag>;
                if (fila.estado === "no_encontrado") {
                    return (
                        <Tag color="red" style={{ whiteSpace: "normal" }}>
                            {fila.motivo}
                        </Tag>
                    );
                }
                if (fila.estado === "ambiguo") {
                    return (
                        <Select
                            size="small"
                            style={{ width: "100%" }}
                            placeholder={`${fila.candidatos.length} insumos con ese ISBN, elige uno`}
                            onChange={(valor) => this.elegirCandidato(fila.fila, valor)}
                        >
                            {fila.candidatos.map((candidato) => (
                                <Option key={String(candidato._id)} value={String(candidato._id)}>
                                    {candidato.stringBuscar || candidato.descripcion}
                                </Option>
                            ))}
                        </Select>
                    );
                }
                return <Tag color="green">Encontrado</Tag>;
            },
        },
    ];

    renderDatosFormato() {
        const { lectura } = this.state;
        if (!lectura) return null;
        const { encabezado, destinos, avisos } = lectura;
        return (
            <>
                <Descriptions size="small" bordered column={3} style={{ marginTop: 15 }}>
                    <Descriptions.Item label="Cliente / Institución">{encabezado.clienteInstitucion || "—"}</Descriptions.Item>
                    <Descriptions.Item label="Canal">{encabezado.canal || "—"}</Descriptions.Item>
                    <Descriptions.Item label="Contacto">{encabezado.contacto || "—"}</Descriptions.Item>
                    <Descriptions.Item label="Teléfono">{encabezado.telefono || "—"}</Descriptions.Item>
                    <Descriptions.Item label="Correo">{encabezado.correo || "—"}</Descriptions.Item>
                    <Descriptions.Item label="Fecha del requerimiento">{fechaVisible(encabezado.fechaRequerimiento)}</Descriptions.Item>
                    <Descriptions.Item label="Fecha requerida de entrega">{fechaVisible(encabezado.fechaEntregaRequerida)}</Descriptions.Item>
                </Descriptions>
                {destinos.length > 0 && (
                    <Table
                        size="small"
                        style={{ marginTop: 10 }}
                        rowKey={(d, i) => i}
                        pagination={false}
                        dataSource={destinos}
                        columns={[
                            { title: "No.", dataIndex: "numero", width: 50, render: (v) => (v === null ? "—" : v) },
                            { title: "Dirección", dataIndex: "direccion" },
                            { title: "Contacto", dataIndex: "contacto" },
                            { title: "Teléfono", dataIndex: "telefono" },
                        ]}
                    />
                )}
                {avisos.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 10 }}
                        message="Revisa en la pestaña Requerimiento después de cargar"
                        description={
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {avisos.map((aviso, i) => (
                                    <li key={i}>{aviso}</li>
                                ))}
                            </ul>
                        }
                    />
                )}
            </>
        );
    }

    render() {
        const { visible, onCancel, hayInsumosCapturados } = this.props;
        const { cargando, progreso, archivoNombre, filas } = this.state;

        const validas = this.filasValidas().length;
        const ambiguas = filas.filter((f) => f.estado === "ambiguo").length;
        const noCargadas = filas.filter((f) => f.estado === "no_encontrado" || f.estado === "error").length;

        return (
            <ModalItem
                title={
                    <span>
                        <FileExcelOutlined /> Subir Formato Único de Requerimiento
                    </span>
                }
                visible={visible}
                onCancel={onCancel}
                width={1100}
                spinning={cargando}
            >
                <div style={{ padding: "0 15px 15px 15px" }}>
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 15 }}
                        message="Sube el formato que llenó el cliente"
                        description="Cada ISBN se busca en el código de barras del insumo y, si no está, en su clave. El precio sale de la lista de precios de la cotización. El canal, el contacto, las fechas, las direcciones de entrega y la edición y el destino de cada renglón quedan en la pestaña Requerimiento."
                    />

                    <ButtonItem
                        icon={<DownloadOutlined />}
                        onClick={() => descargarFormatoRequerimiento(null)}
                        style={{ marginRight: 10 }}
                    >
                        Formato vacío
                    </ButtonItem>

                    <Upload showUploadList={false} customRequest={this.onSubirArchivo} accept=".xlsx,.xls">
                        <ButtonItem icon={<UploadOutlined />}>Subir formato</ButtonItem>
                    </Upload>

                    {archivoNombre && (
                        <Text type="secondary" style={{ marginLeft: 10 }}>
                            {archivoNombre}
                        </Text>
                    )}

                    {cargando && progreso > 0 && <Progress percent={progreso} size="small" style={{ marginTop: 12 }} />}

                    {this.renderDatosFormato()}

                    {filas.length > 0 && (
                        <>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "15px 0 10px" }}>
                                <Text>
                                    {validas} renglón(es) listo(s)
                                    {ambiguas > 0 ? `, ${ambiguas} por definir` : ""}
                                    {noCargadas > 0 ? `, ${noCargadas} no se cargarán` : ""}
                                </Text>
                                {ambiguas + noCargadas > 0 && (
                                    <ButtonItem size="small" icon={<DownloadOutlined />} onClick={this.descargarNoCargados}>
                                        Descargar no cargados
                                    </ButtonItem>
                                )}
                            </div>
                            <Table
                                size="small"
                                rowKey="fila"
                                columns={this.columnasTabla()}
                                dataSource={filas}
                                pagination={{ pageSize: 8 }}
                            />
                        </>
                    )}

                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 15, gap: 10 }}>
                        <ButtonItem onClick={onCancel} rojo>
                            Cancelar
                        </ButtonItem>
                        {hayInsumosCapturados && (
                            <ButtonItem disabled={validas === 0} onClick={() => this.confirmar("reemplazar")}>
                                Reemplazar todo ({validas})
                            </ButtonItem>
                        )}
                        <ButtonItem type="primary" disabled={validas === 0} onClick={() => this.confirmar("agregar")}>
                            {hayInsumosCapturados ? `Agregar al final (${validas})` : `Cargar (${validas})`}
                        </ButtonItem>
                    </div>
                </div>
            </ModalItem>
        );
    }
}

export default ModalFormatoRequerimiento;
